import { useState, useCallback } from 'react';
/**
 * A custom React hook that provides touch and click event handling logic to prevent double invocation of `onClick`.
 * This hook ensures that touch interactions (e.g., on mobile devices) do not trigger duplicate `onClick` events.
 *
 * @example
 * const MyButton = ({ onClick, ...props }) => {
 *   const { handleTouchStart, handleTouchMove, handleTouchEnd } = useTouchClick({ onClick });
 *   return <button
 *      {...props}
 *      onClick={onClick}
 *      onTouchStart={handleTouchStart}
 *      onTouchMove={handleTouchMove}
 *      onTouchEnd={handleTouchEnd}
 *    >
 *      Click Me
 *    </button>;
 * };
 *
 * // Usage
 * <MyButton onClick={() => console.log('Clicked')} />
 */ export const useTouchClick = ({ onClick, onTouchStart, onTouchMove, onTouchEnd })=>{
    const [shouldHandleTap, setShouldHandleTap] = useState(false);
    const handleTouchStart = useCallback((event)=>{
        if (onClick) {
            setShouldHandleTap(true);
        }
        onTouchStart?.(event);
    }, [
        onTouchStart
    ]);
    const handleTouchMove = useCallback((event)=>{
        if (onClick) {
            setShouldHandleTap(false);
        }
        onTouchMove?.(event);
    }, [
        onTouchMove
    ]);
    const handleTouchEnd = useCallback((event)=>{
        onTouchEnd?.(event);
        if (shouldHandleTap) {
            event.preventDefault();
            event.currentTarget.blur();
            setShouldHandleTap(false);
            onClick?.(event);
        }
    }, [
        shouldHandleTap,
        onClick,
        onTouchEnd
    ]);
    return {
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd
    };
};

//# sourceMappingURL=useTouchClick.js.map